import React from 'react';

import useDateFormat from '../../hooks/useDateFormat';
import { formatDisplayDate, formatDisplayDateTime, formatSourceDate } from '../../utils/dateDisplay';
import { normalizeId } from '../../utils/ids';
import UserRef from '../users/UserRef';
import { auditTimes } from './ItemAuditLine';
import ExternalLink from '../common/ExternalLink';

const isHttpUrl = value => typeof value === 'string' && /^https?:\/\//i.test(value);

const Row = ({ label, testId, children }) => (
	<div className="item-info__row" data-testid={testId}>
		<dt className="item-info__label">{label}</dt>
		<dd className="item-info__value">{children}</dd>
	</div>
);

/**
 * The Info card of a saved item (KPR-579): when it was created and last
 * updated, each with who where known, and — for an item a machine client
 * imported — where it came from (`item.source`: system, external id, import
 * date, optional source date and reference). Provenance is something to look
 * up, not to read every time a record opens, so it lives here and not under
 * the title.
 *
 * The read view keeps its own Info list (it holds more, and sits in the
 * accordion); this card is the form's rail. Renders nothing for an item that
 * does not exist yet, or whose dates are unknown.
 */
export default function ItemInfoCard({ item }) {
	useDateFormat(); // re-render when the date-format preference changes
	const { created, updated, changed } = auditTimes(item);
	if (!created) return null;

	const ownerId = normalizeId(item?.owner);
	const updatedById = changed ? normalizeId(item?.updatedBy) : ownerId;
	const source = item?.source && typeof item.source === 'object' ? item.source : null;
	const imported = Boolean(source && (source.system || source.externalId));
	const ref = typeof source?.ref === 'string' ? source.ref.trim() : '';

	const body = (
		<dl className="item-info__list">
			<Row label="Created" testId="item-info-created">
				{formatDisplayDateTime(created.toISOString())}
				{ownerId ? <> <UserRef userId={ownerId} size={16} /></> : null}
			</Row>
			<Row label="Last updated" testId="item-info-updated">
				{formatDisplayDateTime(updated.toISOString())}
				{updatedById ? <> <UserRef userId={updatedById} size={16} /></> : null}
			</Row>
			{imported ? (
				<>
					{source.system ? <Row label="Source system" testId="item-info-system">{source.system}</Row> : null}
					{source.externalId ? (
						<Row label="External id" testId="item-info-external-id">
							<span className="item-info__mono">{source.externalId}</span>
						</Row>
					) : null}
					{source.ingestedAt ? <Row label="Imported" testId="item-info-imported">{formatDisplayDate(source.ingestedAt)}</Row> : null}
					{source.createdAt ? <Row label="Made in source" testId="item-info-source-created">{formatSourceDate(source.createdAt)}</Row> : null}
					{ref ? (
						<Row label="Source reference" testId="item-info-ref">
							{isHttpUrl(ref)
								? <ExternalLink href={ref}>{ref}</ExternalLink>
								: ref}
						</Row>
					) : null}
				</>
			) : null}
		</dl>
	);

	return (
		<section className="form-panel item-info" data-testid="item-info">
			<div className="micro-label item-info__title">Info</div>
			{body}
		</section>
	);
}
